import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { WelletContext } from '../../../context/WelletContext';
import { CircleSpinner } from 'react-spinners-kit';
import { useSelector } from 'react-redux';
import { getQueryString } from '../../../helpers/url';
import { getLocale } from '../../../helpers/dates';
import { useHistory } from 'react-router-dom';
import { format, parseISO, addMonths, differenceInMonths } from 'date-fns';
import Header from '../../../components/Common/Header/Header';
import ContentApp from '../../../components/ContentApp';
import '../../../css/product-list.css';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import EventCard from '../../../components/Common/EventCard/EventCard';
import { Calendar } from 'react-date-range';
import { getEvents } from '../../../services/EventsServices';

const EventSelector = () => {
  const welletContext = useContext(WelletContext);
  const history = useHistory();
  const language = useSelector((state) => state.app.language);
  const locale = getLocale(language);
  const idReservation = getQueryString('id');
  const showId = getQueryString('showId');
  const showName = getQueryString('showName');
  const productCategoryId = getQueryString('productCategoryId');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [nextDate, setNextDate] = useState(new Date());
  const [hasReachedSearchLimit, setHasReachedSearchLimit] = useState(false);
  const [initialDate, setInitialDate] = useState(null);
  const eventDates = new Set(
    events.map((event) => format(parseISO(event.startDate), 'yyyy-MM-dd')),
  );
  const eventRefs = useRef({});
  const observer = useRef();
  const monthRefs = useRef({});
  const maxDate = addMonths(new Date(), 12);
  const loadedMonthsRef = useRef(new Set());

  const fetchEvents = async (date, isScroll = false) => {
    const monthKey = format(date, 'yyyy-MM');
    if (hasReachedSearchLimit || loadedMonthsRef.current.has(format(date, 'yyyy-MM'))) return;
    loadedMonthsRef.current.add(monthKey);
    try {
      const newEvents = await getEvents(welletContext, showId, language, date);

      if (newEvents.length > 0) {
        setEvents((prevEvents) => {
          const existingIds = new Set(prevEvents.map(event => event.id));
          const filteredNewEvents = newEvents.filter(event => !existingIds.has(event.id));
          return [...prevEvents, ...filteredNewEvents];
        });
        loadedMonthsRef.current.add(format(date, 'yyyy-MM'));

        if (!initialDate) setInitialDate(date);
      }

      setTimeout(() => {
        const nextMonth = date < maxDate ? addMonths(date, 1) : date;
        if (
          differenceInMonths(nextMonth, initialDate || date) < 12 &&
          !loadedMonthsRef.current.has(format(nextMonth, 'yyyy-MM'))
        ) {
          setNextDate((prev) =>
            format(prev, 'yyyy-MM') === format(nextMonth, 'yyyy-MM') ? prev : nextMonth,
          );
        } else {
          setHasReachedSearchLimit(true);
        }
      }, 50);
    } catch (error) {
      console.error(error);
      loadedMonthsRef.current.delete(monthKey);
    } finally {
      if (isScroll) setLoadingMore(false);
      else setLoading(false);
    }
  };

  const lastEventRef = useCallback(
    (node) => {
      if (loading || loadingMore) return;

      if (observer.current) observer.current.disconnect();
      const nextMonthYear = format(nextDate, 'yyyy-MM');
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            if (!loadedMonthsRef.current.has(nextMonthYear)) {
              fetchEvents(nextDate, true);
            }
          }
        },
        { threshold: 1 },
      );

      if (node) observer.current.observe(node);
    },
    [loading, loadingMore, nextDate],
  );

  const groupEventsByMonth = (eventsList) => {
    return eventsList.reduce((acc, event) => {
      const monthYear = format(parseISO(event.startDate), 'MMMM yyyy', { locale });
      if (!acc[monthYear]) acc[monthYear] = [];
      acc[monthYear].push(event);
      return acc;
    }, {});
  };

  const isEventDate = (date) => {
    const formattedDate = format(date, 'yyyy-MM-dd');
    return eventDates.has(formattedDate);
  };

  const groupedEvents = groupEventsByMonth(events);

  const handleSelectDate = (date) => {
    setSelectedDate(date);

    const formattedDate = format(date, 'yyyy-MM-dd');
    const event = events.find(
      (event) => format(parseISO(event.startDate), 'yyyy-MM-dd') === formattedDate,
    );

    if (event && eventRefs.current[event.id]) {
      eventRefs.current[event.id].scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const handleMonthChange = (date) => {
    const monthYear = format(date, 'yyyy-MM');
    if (!loadedMonthsRef.current.has(monthYear)) {
      fetchEvents(date);
    }
    if (observer.current) {
      observer.current.disconnect();
      observer.current = null;
    }
  };

  useEffect(() => {
    if (!initialDate) {
      fetchEvents(nextDate);
    }
  }, [nextDate, initialDate]);

  useEffect(() => {
    if (initialDate && !selectedDate) {
      setSelectedDate(initialDate);
    }
  }, [initialDate, selectedDate]);

  const onClickEvent = (event) => {
    const queryParams = new URLSearchParams({
      eventId: event.id,
      showId: event.show.id,
      productCategoryId: productCategoryId,
    });

    if (idReservation !== null && idReservation !== undefined) {
      queryParams.append('id', idReservation);
    }
    history.push({
      pathname: '/ticket-selector',
      search: `?${queryParams.toString()}`,
    });
  };

  return loading && events.length === 0 ? (
    <div className='loaderDiv'>
      <CircleSpinner
        className='spinner-app mx-auto'
        style={{ margin: 'auto' }}
        size={40}
        color='var(--color-font-primary)'
      />
    </div>
  ) : (
    <>
      <Header title={showName} />
      <ContentApp>
        {initialDate && (
          <Calendar
            showMonthAndYearPickers={false}
            maxDate={maxDate}
            minDate={new Date()}
            locale={locale}
            date={selectedDate || initialDate}
            onChange={(date) => {
              if (isEventDate(date)) {
                setSelectedDate(date);
                handleSelectDate(date);
              }
            }}
            onShownDateChange={handleMonthChange}
            dayContentRenderer={(date) => {
              const isAvailable = isEventDate(date);
              return (
                <div
                  style={{
                    color: isAvailable ? 'white' : 'grey',
                  }}
                >
                  {date.getDate()}
                </div>
              );
            }}
            disabled={true}
            navigation={false}
          />
        )}

        {Object.keys(groupedEvents).map((monthYear, monthIndex) => (
          <div key={monthYear} ref={(el) => (monthRefs.current[monthYear] = el)}>
            <div className='text-primary font-1rem fw-semibold sentences-capitalize mt-3'>
              {monthYear}
            </div>
            {groupedEvents[monthYear].map((event, eventIndex) => (
              <div
                key={event.id}
                ref={(el) => {
                  eventRefs.current[event.id] = el;
                  if (
                    Object.keys(groupedEvents).length === monthIndex + 1 &&
                    groupedEvents[monthYear].length === eventIndex + 1
                  ) {
                    lastEventRef(el);
                  }
                }}
              >
                <EventCard
                  onClick={() => event.associatedProducts !== false && onClickEvent(event)}
                  event={event}
                />
              </div>
            ))}
          </div>
        ))}

        {loadingMore && (
          <div className='p-3 d-flex justify-content-center'>
            <CircleSpinner
              className='spinner-app mx-auto'
              style={{ margin: 'auto' }}
              size={40}
              color='var(--color-font-primary)'
            />
          </div>
        )}
      </ContentApp>
    </>
  );
};

export default EventSelector;
